import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Permission } from '../../enums/permissions.enum';
import { MyJwtPayload } from '../../interfaces/user-interfaces';

@Injectable({
  providedIn: 'root',
})
export class JwtService {
  /**
   * Decodes a JWT token and extracts permissions as Permission[].
   * @param token - The JWT token as a string.
   * @returns An array of `Permission` enum values.
   */
  static extractTokenPermissions(token: string): Permission[] {
    try {
      const decoded: any = jwtDecode(token);

      if (!decoded || !decoded.roles) {
        return [];
      }

      // Map only valid permissions from the enum
      return (decoded.roles as string[])
        .filter((perm: string) => {
          if (!(perm in Permission)) {
            console.error(`Unknown permission found in JWT: ${perm}`);
            return false; // Ignore unknown permissions
          }
          return true;
        })
        .map((perm) => perm as Permission);
    } catch (error) {
      console.error('Invalid JWT token:', error);
      return [];
    }
  }

  /**
   * Decodes a JWT token to extract the user’s email (or `sub`) when no full user object is provided
   * so the profile loader always has a valid email to fetch the employee record on first login.
   * Marked `static` because it’s a pure utility that can be called anywhere without needing Angular DI.
   * To be used by auth.reducer in order to avoid null email on first page load after logout and login.
   * (on logout -> login, selectUser is empty, but authentication is populated)
   */
  static getEmailFromToken(token: string): string | undefined {
    try {
      const payload = jwtDecode<MyJwtPayload>(token);
      return (payload as any).email ?? payload.sub;
    } catch (error) {
      console.error('Invalid JWT token when extracting email:', error);
      return undefined;
    }
  }
}
